// Instagram Business Login launcher.
//
// "Instagram API with Instagram login" uses Instagram's own OAuth consent
// screen (not the Facebook JS SDK). We open it in a popup pointed at a static
// callback page (public/instagram-callback.html) which captures the returned
// `code` and postMessages it back to this opener window. We then hand the code
// + the exact redirect_uri to the instagram-onboard edge function, which does
// the token exchange server-side. The token never touches the browser.
//
// Requires:
//   VITE_INSTAGRAM_APP_ID . The Instagram app ID (from the Meta dashboard's
//                            Instagram API "API setup with Instagram login"),
//                            NOT the Facebook/Meta app ID.
// Optional:
//   VITE_INSTAGRAM_OAUTH_REDIRECT. Override the callback URL. Defaults to
//                            `${window.location.origin}/instagram-callback.html`.
//   The redirect URI must be registered in the Meta dashboard under
//   Instagram → "Set up Instagram business login" → OAuth redirect URIs.

const SCOPES = ["instagram_business_basic", "instagram_business_manage_messages"];
// "Reply to comments with a DM" (comment webhooks, private replies, public
// comment replies). Asked for only when the owner sets that workflow up, or
// already granted it, so an unapproved permission can never block an
// ordinary Instagram connect.
export const INSTAGRAM_COMMENTS_SCOPE = "instagram_business_manage_comments";

// instagram.com's OAuth pages send Cross-Origin-Opener-Policy, which detaches
// the popup from window.opener as soon as it navigates to Instagram, so the
// callback page's opener postMessage usually never arrives. It also relays
// through these same-origin channels, which survive the COOP detach.
export const IG_OAUTH_CHANNEL = "ig-oauth";
export const IG_OAUTH_STORAGE_KEY = "ig_oauth_result";
// After the popup closes, wait this long for a late BroadcastChannel/storage
// delivery before declaring the attempt cancelled.
const POPUP_CLOSE_GRACE_MS = 1500;

export interface InstagramOAuthResult {
  code: string;
  redirectUri: string;
}

export type IgOAuthOutcome =
  | { kind: "code"; code: string }
  | { kind: "error"; message: string }
  | null;

/** Interpret a relayed IG_OAUTH payload. Returns null when the payload isn't
 *  ours or its CSRF state doesn't match this launch attempt. */
export function parseIgOAuthRelay(data: unknown, expectedState: string): IgOAuthOutcome {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.type !== "IG_OAUTH") return null;
  if (typeof d.state === "string" && d.state.length > 0 && d.state !== expectedState) {
    return null;
  }
  if (typeof d.error === "string" && d.error) {
    return {
      kind: "error",
      message:
        typeof d.error_description === "string" && d.error_description
          ? d.error_description
          : d.error,
    };
  }
  if (typeof d.code === "string" && d.code) {
    return { kind: "code", code: d.code };
  }
  return { kind: "error", message: "Instagram authorization returned no code." };
}

export function instagramOAuthConfigured(): boolean {
  return Boolean(import.meta.env.VITE_INSTAGRAM_APP_ID);
}

export function getInstagramRedirectUri(): string {
  return (
    (import.meta.env.VITE_INSTAGRAM_OAUTH_REDIRECT as string | undefined) ||
    `${window.location.origin}/instagram-callback.html`
  );
}

/** Open the Instagram OAuth popup and resolve with the auth code + redirect_uri
 *  used (the backend needs the identical redirect_uri for the token exchange).
 *  Rejects if the user closes the popup or Instagram returns an error. */
export async function launchInstagramOAuth(
  options: { comments?: boolean } = {},
): Promise<InstagramOAuthResult> {
  const appId = import.meta.env.VITE_INSTAGRAM_APP_ID as string | undefined;
  if (!appId) {
    throw new Error("Instagram connect isn't configured yet (missing VITE_INSTAGRAM_APP_ID).");
  }

  const redirectUri = getInstagramRedirectUri();
  const state = crypto.randomUUID();
  const authUrl =
    "https://www.instagram.com/oauth/authorize?" +
    new URLSearchParams({
      enable_fb_login: "0",
      force_authentication: "1",
      client_id: appId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: (options.comments ? [...SCOPES, INSTAGRAM_COMMENTS_SCOPE] : SCOPES).join(","),
      state,
    }).toString();

  const w = 600;
  const h = 760;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(
    authUrl,
    "instagram_oauth",
    `width=${w},height=${h},left=${left},top=${top}`,
  );
  if (!popup) {
    throw new Error("Popup blocked. Please allow popups and try again.");
  }

  return await new Promise<InstagramOAuthResult>((resolve, reject) => {
    let settled = false;
    let graceTimer: number | undefined;
    let channel: BroadcastChannel | null = null;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      clearInterval(closedTimer);
      if (graceTimer !== undefined) window.clearTimeout(graceTimer);
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
      try {
        localStorage.removeItem(IG_OAUTH_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    };

    const settle = (outcome: IgOAuthOutcome) => {
      if (!outcome || settled) return;
      settled = true;
      cleanup();
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      if (outcome.kind === "code") {
        resolve({ code: outcome.code, redirectUri });
      } else {
        reject(new Error(outcome.message));
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      settle(parseIgOAuthRelay(event.data, state));
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== IG_OAUTH_STORAGE_KEY || !event.newValue) return;
      try {
        settle(parseIgOAuthRelay(JSON.parse(event.newValue), state));
      } catch {
        /* ignore malformed writes */
      }
    };

    // Direct read for races where the write happened before our listener saw
    // the event (or the storage event never fired). The CSRF state check in
    // parseIgOAuthRelay makes stale leftovers from other attempts inert.
    const readStoredResult = (): IgOAuthOutcome => {
      try {
        const raw = localStorage.getItem(IG_OAUTH_STORAGE_KEY);
        return raw ? parseIgOAuthRelay(JSON.parse(raw), state) : null;
      } catch {
        return null;
      }
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    try {
      channel = new BroadcastChannel(IG_OAUTH_CHANNEL);
      channel.onmessage = (event) => settle(parseIgOAuthRelay(event.data, state));
    } catch {
      channel = null; // BroadcastChannel unavailable — storage path still works
    }

    // Detect the popup closing. The callback page closes itself right after
    // relaying, so give its BroadcastChannel/storage delivery a short grace
    // window before treating the close as a manual cancel.
    const closedTimer = setInterval(() => {
      if (!popup.closed || settled || graceTimer !== undefined) return;
      settle(readStoredResult());
      if (settled) return;
      graceTimer = window.setTimeout(() => {
        settle(readStoredResult());
        if (!settled) {
          settled = true;
          cleanup();
          reject(new Error("Authorization was cancelled."));
        }
      }, POPUP_CLOSE_GRACE_MS);
    }, 500);
  });
}
